import { Button, Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableInstance } from 'react-table';

import { ReactComponent as BillingIcon } from '@work4all/assets/icons/billing-icon.svg';

import { GroupedItem } from '@work4all/components/lib/dataDisplay/basic-table/hooks/query-table-data/types';
import { relativeDateToDate } from '@work4all/components/lib/dataDisplay/basic-table/utils/filters/filterConversion';
import { NavigationOverlay } from '@work4all/components/lib/navigation/navigation-overlay';

import { checkModuleRight, useModuleRights } from '@work4all/data';

import { TimeTracking } from '@work4all/models/lib/Classes/TimeTracking.entity';
import { ModuleAccessRightType } from '@work4all/models/lib/Enums/ModuleAccessRightType.enum';

import { ProjectHourBillingOverlay } from './project-hours-table/project-hour-billing-overlay';

export type IProjectBillingCellProps = {
  value: string;
  row: { original: TimeTracking };
  table: TableInstance;
};

export const ProjectBillingCell = (props: IProjectBillingCellProps) => {
  const { value, row, table } = props;
  const { t } = useTranslation();

  //eslint-disable-next-line
  //@ts-ignore
  const meta: GroupedItem = row?.original?.meta;
  const projectId = Number(meta?.groupByVal);

  const rowSelection = table.getState().rowSelection;
  const projectTimeIds = Object.keys(rowSelection)
    .filter((key) => {
      return Number(key.split('.')[0]) === projectId;
    })
    .map((key) => Number(key.split('.')[key.split('.').length - 1]));

  const [startDate, endDate] = useMemo<string[]>(() => {
    const dateFilter = table
      .getState()
      .columnFilters.find((f) => f.id === 'date');
    if (dateFilter) {
      const { startDate, endDate } = relativeDateToDate(
        dateFilter.value.value.startDate,
        dateFilter.value.value.endDate
      );
      return [
        startDate?.toFormat('yyyy-MM-dd'),
        endDate?.toFormat('yyyy-MM-dd'),
      ];
    }

    return [undefined, undefined];
  }, [table]);

  const [isBillingOverlayOpen, setIsBillingOverlayOpen] = useState(false);

  const { rights } = useModuleRights();
  const canAccessBilling = checkModuleRight(
    rights,
    ModuleAccessRightType.FEATURE_PROJECT_BILLING
  );

  const handleOnClick = useCallback(() => {
    setIsBillingOverlayOpen(true);
  }, []);

  return (
    <>
      <NavigationOverlay
        open={isBillingOverlayOpen}
        initialView={{
          view: (
            <ProjectHourBillingOverlay
              projectTimeIds={projectTimeIds}
              id={projectId}
              startDate={startDate}
              endDate={endDate}
              onAfterSave={() => {
                setIsBillingOverlayOpen(false);
              }}
            />
          ),
        }}
        close={() => {
          setIsBillingOverlayOpen(false);
        }}
      />
      <Stack direction="row" alignItems="center" gap="0.5rem">
        {canAccessBilling && (
          <Button startIcon={<BillingIcon />} onClick={handleOnClick}>
            {t('MASK.BILLING')}
          </Button>
        )}
        <div>{value || meta?.groupByLabel}</div>
      </Stack>
    </>
  );
};
